import { useRef, useCallback } from 'react';
import { WebSocketMessage } from '../types';

interface UseWebSocketProps {
  onMessage: (message: WebSocketMessage) => void;
  onClose: () => void;
  onError: () => void;
}

export const useWebSocket = ({ onMessage, onClose, onError }: UseWebSocketProps) => {
  const wsRef = useRef<WebSocket | null>(null);
  const isConnectingRef = useRef(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const connect = useCallback(async () => {
    if (wsRef.current?.readyState === WebSocket.OPEN || isConnectingRef.current) {
      console.log('WebSocket already connected or connecting');
      return;
    }

    isConnectingRef.current = true;
    console.log('Attempting WebSocket connection...');

    try {
      // Try multiple WebSocket URLs
      const wsUrls = [
        `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/api/chat`,
        `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/functions/api/chat`
      ];
      
      let ws: WebSocket | null = null;
      let connected = false;
      
      for (const wsUrl of wsUrls) {
        console.log('Trying WebSocket URL:', wsUrl);
        try {
          ws = new WebSocket(wsUrl);
          
          // Wait for connection or error
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              console.log('WebSocket connection timeout for:', wsUrl);
              reject(new Error('Connection timeout'));
            }, 3000); // Reduced timeout
            
            ws!.onopen = () => {
              clearTimeout(timeout);
              connected = true;
              console.log('WebSocket connected to:', wsUrl);
              resolve(true);
            };
            
            ws!.onerror = () => {
              clearTimeout(timeout);
              console.log('WebSocket connection failed for:', wsUrl);
              reject(new Error('Connection failed'));
            };
          });
          
          if (connected) break;
        } catch (err) {
          console.log(`Failed to connect to ${wsUrl}:`, err);
          ws?.close();
          ws = null;
        }
      }
      
      if (!ws || !connected) {
        console.error('All WebSocket connection attempts failed');
        throw new Error('All WebSocket connection attempts failed');
      }
      
      ws.onopen = () => {
        isConnectingRef.current = false;
        console.log('WebSocket connected successfully');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data.type);
          onMessage(data);
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };
      
      ws.onclose = () => {
        isConnectingRef.current = false;
        wsRef.current = null;
        console.log('WebSocket connection closed');
        onClose();
        
        // Auto-reconnect after 2 seconds
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Attempting to reconnect...');
          connect();
        }, 2000);
      };
      
      ws.onerror = () => {
        isConnectingRef.current = false;
        console.error('WebSocket error occurred');
        onError();
      };

      wsRef.current = ws;
    } catch (err) {
      isConnectingRef.current = false;
      console.error('WebSocket connection failed:', err);
      onError();
    }
  }, [onMessage, onClose, onError]);

  const disconnect = useCallback(() => {
    console.log('Disconnecting WebSocket');
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    isConnectingRef.current = false;
  }, []);

  const sendMessage = useCallback((message: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('Sending WebSocket message:', message.type);
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, message not sent:', message);
    }
  }, []);

  return {
    connect,
    disconnect,
    sendMessage,
    isConnecting: isConnectingRef.current
  };
};